import {
  AdminUsersApiError,
  changeAdminUserStatus,
  createAdminUser,
  getAdminUsers,
  getUserRoleAssignment,
  replaceUserRoles,
  type AdminUserStatus,
  type AdminUserSummary,
  type UserRoleAssignmentResponse,
} from '@urbanik/api-client';
import {
  Button,
  CanonicalCard,
  CanonicalDefinitionList,
  CanonicalFilterBar,
  CanonicalFilterField,
  CanonicalMetricCard,
  CanonicalMetricGrid,
  CanonicalModal,
  CanonicalNotice,
  CanonicalPageHeader,
  CanonicalStatus,
  DataTable,
  DataTableFooter,
  FilterActions,
  FormActions,
  FormSection,
  IconButton,
  PageSizeSelector,
  Pagination,
  PermissionGate,
  PersonCell,
  ResultsCounter,
  RowActions,
  RowSelection,
  SearchField,
  SelectField,
  StatePanel,
  TextAreaField,
  TextField,
  canonicalStates,
  formatDateTime,
  type DataColumn,
} from '@urbanik/ui';
import {
  Eye,
  FunnelX,
  KeyRound,
  ListFilter,
  Power,
  PowerOff,
  ShieldCheck,
  UserPlus,
} from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { apiBase } from './api-base';
import { InitialCredentialModal } from './InitialCredentialModal';

type PendingStatus = Readonly<{ user: AdminUserSummary; status: 'active' | 'disabled' }>;
const statusLabel: Record<AdminUserStatus, string> = {
  provisioned: 'Provisionada',
  active: 'Activa',
  disabled: 'Desactivada',
};

export function AdminUsersView({ permissions }: { permissions: readonly string[] }) {
  const [items, setItems] = useState<readonly AdminUserSummary[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<AdminUserStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [createOpen, setCreateOpen] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [state, setState] = useState<'loading' | 'ready' | 'empty' | 'error'>('loading');
  const [reload, setReload] = useState(0);
  const [selected, setSelected] = useState<AdminUserSummary | null>(null);
  const [pendingStatus, setPendingStatus] = useState<PendingStatus | null>(null);
  const [credentialUser, setCredentialUser] = useState<AdminUserSummary | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [roleAssignment, setRoleAssignment] = useState<UserRoleAssignmentResponse | null>(null);
  const [roleUser, setRoleUser] = useState<AdminUserSummary | null>(null);
  const [roleIds, setRoleIds] = useState<readonly string[]>([]);
  const [roleReason, setRoleReason] = useState('');
  const [roleState, setRoleState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [notice, setNotice] = useState<{ message: string; tone: 'success' | 'error' } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    getAdminUsers(apiBase, { search, status, page, pageSize }, controller.signal)
      .then((response) => {
        setItems(response.items);
        setTotalPages(response.totalPages);
        setTotal(response.total);
        setState(response.items.length ? 'ready' : 'empty');
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setState('error');
        if (error instanceof AdminUsersApiError && error.kind === 'unauthorized')
          window.location.hash = '#login';
      });
    return () => controller.abort();
  }, [search, status, page, pageSize, reload]);

  function refresh(message: string) {
    setNotice({ message, tone: 'success' });
    setSelected(null);
    setPendingStatus(null);
    setReason('');
    setReload((value) => value + 1);
  }
  function report(error: unknown) {
    setNotice({
      message: error instanceof Error ? error.message : 'No pudimos completar la operación.',
      tone: 'error',
    });
  }
  async function submitCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const values = new FormData(form);
    setBusy(true);
    try {
      await createAdminUser(apiBase, {
        email: String(values.get('email') ?? ''),
        displayName: String(values.get('displayName') ?? ''),
      });
      form.reset();
      setCreateOpen(false);
      refresh('Cuenta provisionada correctamente.');
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  }
  async function openRoles(user: AdminUserSummary) {
    setRoleUser(user);
    setRoleAssignment(null);
    setRoleIds([]);
    setRoleReason('');
    setRoleState('loading');
    try {
      const assignment = await getUserRoleAssignment(apiBase, user.id);
      setRoleAssignment(assignment);
      setRoleIds(assignment.roles.filter((role) => role.assigned).map((role) => role.id));
      setRoleState('idle');
    } catch (error) {
      setRoleState('error');
      report(error);
    }
  }
  async function saveRoles() {
    if (!roleAssignment || !roleReason.trim()) return;
    setBusy(true);
    try {
      await replaceUserRoles(apiBase, roleAssignment.userId, {
        roleIds,
        expectedVersion: roleAssignment.rolesVersion,
        reason: roleReason.trim(),
      });
      setRoleUser(null);
      setRoleAssignment(null);
      setRoleReason('');
      refresh('Roles actualizados; las sesiones previas del usuario fueron revocadas.');
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  }
  async function confirmStatus() {
    if (!pendingStatus || (pendingStatus.status === 'disabled' && !reason.trim())) return;
    setBusy(true);
    try {
      await changeAdminUserStatus(apiBase, pendingStatus.user.id, {
        status: pendingStatus.status,
        expectedVersion: pendingStatus.user.version,
        ...(pendingStatus.status === 'disabled' ? { reason: reason.trim() } : {}),
      });
      refresh(
        pendingStatus.status === 'disabled'
          ? 'Cuenta desactivada y sesiones revocadas.'
          : 'Cuenta activada.',
      );
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  }

  const columns = useMemo<readonly DataColumn<AdminUserSummary>[]>(
    () => [
      {
        id: 'user',
        header: 'Usuario',
        accessor: (user) => `${user.displayName} ${user.email}`,
        cell: (user) => <PersonCell name={user.displayName} secondary={user.email} />,
      },
      {
        id: 'status',
        header: 'Estado',
        accessor: (user) => statusLabel[user.status],
        cell: (user) => (
          <CanonicalStatus
            tone={
              user.status === 'active'
                ? 'success'
                : user.status === 'disabled'
                  ? 'danger'
                  : 'warning'
            }
          >
            {statusLabel[user.status]}
          </CanonicalStatus>
        ),
      },
      { id: 'roles', header: 'Roles', accessor: (user) => user.roles.join(', ') || 'Sin roles' },
      {
        id: 'lastAccess',
        header: 'Último acceso',
        accessor: (user) =>
          user.lastAccessAt ? formatDateTime(user.lastAccessAt) : 'Sin acceso registrado',
      },
      {
        id: 'actions',
        header: 'Acciones',
        accessor: () => '',
        cell: (user) => (
          <RowActions>
            <IconButton
              type="button"
              label={`Ver detalle de ${user.displayName}`}
              onClick={() => setSelected(user)}
            >
              <Eye aria-hidden="true" />
            </IconButton>
            <PermissionGate
              permission="identity.users.credentials.manage"
              permissions={permissions}
            >
              <IconButton
                type="button"
                label={`Generar credencial de ${user.displayName}`}
                onClick={() => setCredentialUser(user)}
              >
                <KeyRound aria-hidden="true" />
              </IconButton>
            </PermissionGate>
            <PermissionGate permission="identity.user_roles.manage" permissions={permissions}>
              <IconButton
                type="button"
                label={`Administrar roles de ${user.displayName}`}
                onClick={() => void openRoles(user)}
              >
                <ShieldCheck aria-hidden="true" />
              </IconButton>
            </PermissionGate>{' '}
            <PermissionGate permission="identity.users.status.manage" permissions={permissions}>
              <IconButton
                type="button"
                label={`${user.status === 'disabled' ? 'Activar' : 'Desactivar'} ${user.displayName}`}
                onClick={() => {
                  setReason('');
                  setPendingStatus({
                    user,
                    status: user.status === 'disabled' ? 'active' : 'disabled',
                  });
                }}
              >
                {user.status === 'disabled' ? (
                  <Power aria-hidden="true" />
                ) : (
                  <PowerOff aria-hidden="true" />
                )}
              </IconButton>
            </PermissionGate>
          </RowActions>
        ),
      },
    ],
    [permissions],
  );

  const selectedRoles = roleAssignment?.roles.filter((role) => roleIds.includes(role.id)) ?? [];
  const resultingPermissions = [
    ...new Set(selectedRoles.flatMap((role) => role.permissions)),
  ].sort();

  return (
    <div>
      <CanonicalPageHeader
        title="Usuarios y estado de acceso"
        description="Consulta, provisiona y administra asignaciones de roles con permisos efectivos de solo lectura."
        actions={
          <PermissionGate permission="identity.users.create" permissions={permissions}>
            <Button
              type="button"
              variant="primary"
              icon={<UserPlus aria-hidden="true" />}
              onClick={() => setCreateOpen(true)}
            >
              Crear cuenta
            </Button>
          </PermissionGate>
        }
      />
      <CanonicalNotice
        message={notice?.message ?? ''}
        tone={notice?.tone ?? 'success'}
        onDismiss={() => setNotice(null)}
      />
      <CanonicalCard title="Cuentas persistidas">
        <CanonicalFilterBar>
          <SearchField
            value={search}
            label="Buscar por correo o nombre"
            placeholder="Correo o nombre"
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
          />
          <CanonicalFilterField icon={<ListFilter aria-hidden="true" />}>
            <SelectField
              label="Estado"
              value={status}
              options={[
                { value: 'all', label: 'Todos' },
                { value: 'provisioned', label: 'Provisionada' },
                { value: 'active', label: 'Activa' },
                { value: 'disabled', label: 'Desactivada' },
              ]}
              onValueChange={(value) => {
                setStatus(value as AdminUserStatus | 'all');
                setPage(1);
              }}
            />
          </CanonicalFilterField>
          {search || status !== 'all' ? (
            <FilterActions>
              <Button
                type="button"
                icon={<FunnelX aria-hidden="true" />}
                onClick={() => {
                  setSearch('');
                  setStatus('all');
                  setPage(1);
                }}
              >
                Limpiar filtros
              </Button>
            </FilterActions>
          ) : null}
        </CanonicalFilterBar>
        {state === 'loading' ? (
          <StatePanel state={canonicalStates.find((item) => item.kind === 'loading')!} />
        ) : null}
        {state === 'error' ? (
          <StatePanel
            state={canonicalStates.find((item) => item.kind === 'error')!}
            onAction={() => setReload((value) => value + 1)}
          />
        ) : null}
        {state === 'empty' ? (
          <StatePanel state={canonicalStates.find((item) => item.kind === 'empty')!} />
        ) : null}
        {state === 'ready' ? (
          <DataTable
            paginate={false}
            rows={items}
            columns={columns}
            visibleColumnIds={new Set(columns.map((column) => column.id))}
            sort={{ columnId: 'user', direction: 'ascending' }}
            onSort={() => undefined}
            empty={<StatePanel state={canonicalStates.find((item) => item.kind === 'empty')!} />}
          />
        ) : null}
        <DataTableFooter>
          <ResultsCounter
            from={items.length ? (page - 1) * pageSize + 1 : 0}
            to={(page - 1) * pageSize + items.length}
            total={total}
            noun="usuarios"
          />
          <Pagination page={page} pages={totalPages} onChange={setPage} />
          <PageSizeSelector
            value={pageSize}
            options={[10, 25, 50]}
            onChange={(value) => {
              setPageSize(value);
              setPage(1);
            }}
          />
        </DataTableFooter>
      </CanonicalCard>
      <CanonicalModal
        open={createOpen}
        title="Provisionar cuenta"
        eyebrow="Nueva cuenta administrativa"
        description="La cuenta se crea sin credencial ni roles."
        size="medium"
        onClose={() => !busy && setCreateOpen(false)}
      >
        <form onSubmit={submitCreate}>
          <FormSection
            title="Identidad de la cuenta"
            description="Datos mínimos para provisionar el usuario."
          >
            <TextField
              name="displayName"
              label="Nombre visible"
              required
              minLength={2}
              maxLength={120}
            />
            <TextField
              name="email"
              label="Correo electrónico"
              required
              type="email"
              autoComplete="off"
            />
          </FormSection>
          <FormActions>
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => setCreateOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={busy}
              icon={<UserPlus aria-hidden="true" />}
            >
              {busy ? 'Creando…' : 'Crear cuenta'}
            </Button>
          </FormActions>
        </form>
      </CanonicalModal>
      <CanonicalModal
        open={selected !== null}
        title={selected?.displayName ?? 'Detalle de usuario'}
        eyebrow="Detalle de cuenta"
        description={selected?.email ?? ''}
        onClose={() => setSelected(null)}
      >
        {selected ? (
          <>
            <CanonicalDefinitionList
              label="Datos de la cuenta"
              items={[
                { label: 'Estado', value: statusLabel[selected.status] },
                { label: 'Versión', value: selected.version },
                { label: 'Creada', value: formatDateTime(selected.createdAt) },
                { label: 'Roles asignados', value: selected.roles.join(', ') || 'Sin roles' },
                {
                  label: 'Permisos efectivos (solo lectura)',
                  value: selected.permissions.join(', ') || 'Sin permisos',
                },
                {
                  label: 'Motivo de desactivación',
                  value: selected.deactivationReason || 'No aplica',
                },
              ]}
            />
          </>
        ) : null}
      </CanonicalModal>

      <CanonicalModal
        open={roleUser !== null}
        title="Asignar roles"
        eyebrow="CR-M02-001.2"
        description={roleUser ? `${roleUser.displayName} · ${roleUser.email}` : ''}
        size="large"
        onClose={() => !busy && setRoleUser(null)}
      >
        {roleState === 'loading' ? (
          <StatePanel state={canonicalStates.find((item) => item.kind === 'loading')!} />
        ) : null}
        {roleState === 'error' ? (
          <StatePanel
            state={canonicalStates.find((item) => item.kind === 'error')!}
            onAction={() => roleUser && void openRoles(roleUser)}
          />
        ) : null}
        {roleAssignment ? (
          <>
            <FormSection
              title="Selecciona los roles"
              description="Marca cada rol que necesite el usuario. La asignación se aplicará al guardar."
            >
              <DataTable
                rows={roleAssignment.roles}
                columns={[
                  {
                    id: 'selection',
                    header: 'Asignar',
                    accessor: (role) => (roleIds.includes(role.id) ? 'Asignado' : 'No asignado'),
                    cell: (role) => (
                      <RowSelection
                        checked={roleIds.includes(role.id)}
                        label={`${roleIds.includes(role.id) ? 'Retirar' : 'Asignar'} rol ${role.name}`}
                        onChange={(checked) =>
                          setRoleIds((current) =>
                            checked
                              ? [...new Set([...current, role.id])]
                              : current.filter((id) => id !== role.id),
                          )
                        }
                      />
                    ),
                  },
                  {
                    id: 'role',
                    header: 'Rol',
                    accessor: (role) => `${role.name} ${role.description}`,
                    cell: (role) => (
                      <PersonCell
                        name={role.name}
                        secondary={`${role.code} · ${role.description}`}
                      />
                    ),
                  },
                  {
                    id: 'permissions',
                    header: 'Permisos',
                    accessor: (role) => String(role.permissions.length),
                    cell: (role) => `${role.permissions.length} incluidos`,
                  },
                  {
                    id: 'status',
                    header: 'Estado',
                    accessor: (role) => (roleIds.includes(role.id) ? 'Asignado' : 'Disponible'),
                    cell: (role) => (
                      <CanonicalStatus tone={roleIds.includes(role.id) ? 'success' : 'neutral'}>
                        {roleIds.includes(role.id) ? 'Asignado' : 'Disponible'}
                      </CanonicalStatus>
                    ),
                  },
                ]}
                visibleColumnIds={new Set(['selection', 'role', 'permissions', 'status'])}
                sort={{ columnId: 'role', direction: 'ascending' }}
                onSort={() => undefined}
                empty={
                  <StatePanel state={canonicalStates.find((item) => item.kind === 'empty')!} />
                }
              />
            </FormSection>
            <CanonicalMetricGrid>
              <CanonicalMetricCard
                label="Roles seleccionados"
                value={selectedRoles.length}
                detail={`de ${roleAssignment.roles.length} disponibles`}
                tone={selectedRoles.length ? 'success' : 'warning'}
              />
              <CanonicalMetricCard
                label="Permisos resultantes"
                value={resultingPermissions.length}
                detail="calculados a partir de los roles seleccionados"
                tone="info"
              />
            </CanonicalMetricGrid>
            <CanonicalDefinitionList
              label="Resumen de la asignación"
              columns={1}
              items={[
                {
                  label: 'Roles que se guardarán',
                  value: selectedRoles.map((role) => role.name).join(', ') || 'Sin roles',
                },
                {
                  label: 'Permisos efectivos antes del cambio',
                  value: roleAssignment.effectivePermissions.join(', ') || 'Sin permisos efectivos',
                },
                {
                  label: 'Permisos resultantes',
                  value: resultingPermissions.join(', ') || 'Sin permisos efectivos',
                },
              ]}
            />
            <TextAreaField
              label="Motivo del cambio"
              required
              value={roleReason}
              onChange={(event) => setRoleReason(event.target.value)}
              hint="El cambio quedará auditado y revocará las sesiones activas del usuario."
              rows={3}
            />
            <FormActions>
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => setRoleUser(null)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={busy || !roleReason.trim()}
                icon={<ShieldCheck aria-hidden="true" />}
                onClick={() => void saveRoles()}
              >
                {busy ? 'Guardando…' : 'Guardar roles'}
              </Button>
            </FormActions>
          </>
        ) : null}
      </CanonicalModal>
      <InitialCredentialModal
        user={credentialUser}
        onClose={() => setCredentialUser(null)}
        onIssued={() => setReload((value) => value + 1)}
      />
      <CanonicalModal
        open={pendingStatus !== null}
        title={pendingStatus?.status === 'disabled' ? 'Desactivar cuenta' : 'Activar cuenta'}
        eyebrow="Confirmación requerida"
        description={
          pendingStatus ? `${pendingStatus.user.displayName} · ${pendingStatus.user.email}` : ''
        }
        size="medium"
        onClose={() => !busy && setPendingStatus(null)}
      >
        {pendingStatus?.status === 'disabled' ? (
          <TextAreaField
            label="Motivo de desactivación"
            required
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            hint="También se revocarán todas las sesiones activas."
            rows={4}
          />
        ) : (
          <CanonicalCard>
            La activación no restaura sesiones ni asigna roles o credenciales.
          </CanonicalCard>
        )}
        <FormActions>
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => setPendingStatus(null)}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant={pendingStatus?.status === 'disabled' ? 'danger' : 'primary'}
            disabled={busy || (pendingStatus?.status === 'disabled' && !reason.trim())}
            onClick={() => void confirmStatus()}
          >
            {busy ? 'Procesando…' : 'Confirmar'}
          </Button>
        </FormActions>
      </CanonicalModal>
    </div>
  );
}
